import { ConsoleLogger } from '../internal/ConsoleLogger';
import { IsProduction } from './EnvProcess';

/** Same singleton the package exports as `Logger`; GetInstance is idempotent so this resolves to the one shared logger rather than a second instance. */
const logger = ConsoleLogger.GetInstance(!IsProduction);

/** File-scope constants for the retry helper. */
const localConstants = {
  // Exponential growth factor applied to `baseDelayMs` per elapsed attempt: delay before retry n (0-indexed) is baseDelayMs * 3^n (e.g. 500 -> 1500 -> 4500).
  backoffFactor: 3
} as const;

/** Options describing a bounded exponential-backoff retry. */
export interface IRetryWithBackoffOptions {
  /** Total attempts to make (must be >= 1). The operation runs at most this many times. */
  attempts: number;
  /** Base backoff in milliseconds; the delay before the nth retry (0-indexed) is `baseDelayMs * 3^n`. */
  baseDelayMs: number;
  /** Log context label for the per-attempt Debug lines. */
  logContext: string;
}

/**
 * Runs an async operation with bounded exponential-backoff retries. Each failure is logged at Debug and retried after `baseDelayMs * 3^(attemptIndex)`; the last error is rethrown once every
 * attempt is exhausted. Used where a multi-step cleanup must fully succeed (or fully fail) before a dependent commit - e.g. releasing provider resources and Mongo/storage data before a tenant's
 * PostgreSQL rows are deleted, so nothing is deleted unless the external cleanup first succeeds.
 *
 * @param operation - The async operation to attempt. Must be safe to re-run (idempotent) since a later attempt may repeat partially-applied work.
 * @param options - Retry tuning (attempts, base delay, log context).
 * @returns The operation result once it succeeds.
 * @throws The last error when all attempts fail.
 */
export async function RetryWithBackoff<T>(operation: () => Promise<T>, options: IRetryWithBackoffOptions): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < options.attempts; attempt++) {
    try {
      return await operation();
    } catch (err: unknown) {
      lastError = err;
      const isLastAttempt = attempt === options.attempts - 1;
      logger.Debug(options.logContext, `Attempt ${attempt + 1}/${options.attempts} failed: ${logger.GetErrorMessage(err)}${isLastAttempt ? '' : '; retrying'}`);
      if (isLastAttempt) break;
      const delayMs = options.baseDelayMs * Math.pow(localConstants.backoffFactor, attempt);
      await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
    }
  }
  throw lastError;
}
